import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarDays, CheckCircle2, Clock3, Plus, Search, XCircle } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import Modal from '../../components/ui/Modal';
import { api } from '../../lib/api';
import { normalizeReservation, toBackendReservationStatus } from '../../lib/backendAdapters';
import { useAuthStore } from '../../store/authStore';
import { formatDate } from '../../lib/mockData';
import { persistence } from '../../lib/persistence';
import { WifiOff, ShieldCheck } from 'lucide-react';


const statusMeta = {
    pending: { label: 'Pendente', className: 'badge-warning' },
    confirmed: { label: 'Confirmada', className: 'badge-success' },
    cancelled: { label: 'Cancelada', className: 'badge-destructive' },
    completed: { label: 'Concluida', className: 'badge-muted' },
} as const;

const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

const defaultOperatingHours = {
    sunday: { open: '11:00', close: '22:00', closed: false },
    monday: { open: '11:00', close: '22:00', closed: false },
    tuesday: { open: '11:00', close: '22:00', closed: false },
    wednesday: { open: '11:00', close: '22:00', closed: false },
    thursday: { open: '11:00', close: '22:00', closed: false },
    friday: { open: '11:00', close: '23:00', closed: false },
    saturday: { open: '11:00', close: '23:00', closed: false },
};

const toMinutes = (value: string) => {
    const [hours, minutes] = value.split(':').map(Number);
    return (hours * 60) + minutes;
};

const toTimeLabel = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const AdminReservations: React.FC = () => {
    const { user } = useAuthStore();
    const [reservations, setReservations] = useState<any[]>([]);
    const [tables, setTables] = useState<any[]>([]);
    const [operatingHours, setOperatingHours] = useState<any>(defaultOperatingHours);
    const [slotMinutes, setSlotMinutes] = useState(30);
    const [search, setSearch] = useState('');
    const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [reservationModuleEnabled, setReservationModuleEnabled] = useState(true);
    const [tableModuleEnabled, setTableModuleEnabled] = useState(true);
    const [reservationSaving, setReservationSaving] = useState(false);
    const [updatingReservationAction, setUpdatingReservationAction] = useState<string | null>(null);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    const [lastSync, setLastSync] = useState<string | null>(persistence.getLastSync());


    const loadReservations = useCallback(async () => {
        if (!user?.establishmentId) return;
        try {
            setLoading(true);
            const [reservationsResponse, establishmentResponse] = await Promise.all([
                api.get(`/reservations/establishment/${user.establishmentId}`),
                api.get(`/establishments/id/${user.establishmentId}`),
            ]);
            setReservations(reservationsResponse.data.map(normalizeReservation));
            setTables(establishmentResponse.data.tables ?? []);
            setOperatingHours(establishmentResponse.data.settings?.operatingHours ?? defaultOperatingHours);
            setSlotMinutes(Number(establishmentResponse.data.settings?.reservationSlotMinutes) || 30);
            setReservationModuleEnabled(establishmentResponse.data.settings?.hasReservationModule !== false);
            setTableModuleEnabled(establishmentResponse.data.settings?.hasTableModule !== false);

            // Persistir para segurança offline
            persistence.save('RESERVATIONS', reservationsResponse.data.map(normalizeReservation));
            // Também salvamos metadados importantes como mesas e horários
            persistence.save('METRICS', { ...persistence.load('METRICS'), tables: establishmentResponse.data.tables });
            
            setLastSync(new Date().toISOString());
            setIsOffline(false);
        } catch (error) {
            console.error('Failed to load reservations', error);
            setIsOffline(true);
        } finally {
            setLoading(false);
        }


    }, [user?.establishmentId]);

    useEffect(() => {
        // Carga inicial do cache (segurança offline)
        const cachedReservations = persistence.load<any[]>('RESERVATIONS');
        if (cachedReservations) {
            setReservations(cachedReservations);
        }

        loadReservations();

        const handleOnline = () => setIsOffline(false);
        const handleOffline = () => setIsOffline(true);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadReservations]);



    const filteredReservations = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return reservations;
        return reservations.filter((reservation) =>
            reservation.customerName?.toLowerCase().includes(term)
            || reservation.customerPhone?.toLowerCase().includes(term),
        );
    }, [reservations, search]);

    const reservationsForSelectedDate = useMemo(
        () => reservations.filter((reservation) => reservation.date.slice(0, 10) === selectedDate),
        [reservations, selectedDate],
    );

    const agendaConfig = useMemo(() => {
        const dayIndex = new Date(`${selectedDate}T12:00:00`).getDay();
        const dayKey = dayKeys[dayIndex];
        const config = operatingHours[dayKey] ?? defaultOperatingHours[dayKey];
        if (!config || config.closed) {
            return null;
        }

        const open = toMinutes(config.open);
        const close = toMinutes(config.close);
        const slots: string[] = [];
        for (let current = open; current < close; current += slotMinutes) {
            slots.push(toTimeLabel(current));
        }

        return {
            open: config.open,
            close: config.close,
            slots,
        };
    }, [operatingHours, selectedDate, slotMinutes]);

    const agendaRows = useMemo(() => {
        if (!agendaConfig) return [];
        return agendaConfig.slots.map((slot) => {
            const slotReservations = reservationsForSelectedDate.filter((reservation) => reservation.time === slot);
            return {
                slot,
                occupancy: slotReservations.length,
                totalGuests: slotReservations.reduce((sum, reservation) => sum + reservation.partySize, 0),
                tables: tables.map((table) => ({
                    tableId: table.id,
                    tableLabel: table.label,
                    reservation: slotReservations.find((reservation) => reservation.table?.id === table.id) || null,
                })),
            };
        });
    }, [agendaConfig, reservationsForSelectedDate, tables]);

    if (!loading && !reservationModuleEnabled) {
        const fallbackRoute = user?.role === 'attendant' ? '/staff' : '/admin';
        return <Navigate to={fallbackRoute} replace />;
    }

    const updateStatus = async (id: string, status: 'pending' | 'confirmed' | 'cancelled' | 'completed') => {
        try {
            setUpdatingReservationAction(`${id}:${status}`);
            await api.patch(`/reservations/${id}/status`, {
                status: toBackendReservationStatus(status),
            });
            await loadReservations();
        } catch (error) {
            console.error('Failed to update reservation status', error);
        } finally {
            setUpdatingReservationAction(null);
        }
    };

    const createReservation = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!user?.establishmentId) return;

        const formData = new FormData(e.currentTarget);
        const date = formData.get('date') as string;
        const time = formData.get('time') as string;

        try {
            setReservationSaving(true);
            await api.post('/reservations', {
                customerName: formData.get('customerName'),
                customerPhone: formData.get('customerPhone'),
                guests: Number(formData.get('guests')),
                notes: formData.get('notes') || undefined,
                establishmentId: user.establishmentId,
                date: new Date(`${date}T${time}:00`).toISOString(),
            });
            setModalOpen(false);
            await loadReservations();
        } catch (error) {
            console.error('Failed to create reservation', error);
        } finally {
            setReservationSaving(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <div>
                    <h1 className="text-2xl font-bold text-white">Reservas</h1>
                    <p className="mt-1 text-sm text-muted-foreground">Gerencie confirmacoes e agenda do salao.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    {isOffline ? (
                        <div className="flex flex-col items-end">
                            <span className="badge flex items-center gap-1.5 border-amber-500/50 bg-amber-500/10 text-amber-500">
                                <WifiOff className="h-3.5 w-3.5" />
                                Modo Offline de Segurança
                            </span>
                            {lastSync && (
                                <span className="mt-1 text-[10px] text-amber-200/50">
                                    Última sincronização: {new Date(lastSync).toLocaleTimeString('pt-BR')}
                                </span>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col items-end">
                            <span className="badge flex items-center gap-1.5 border-emerald-500/50 bg-emerald-500/10 text-emerald-400">
                                <ShieldCheck className="h-3.5 w-3.5" />
                                Agenda Sincronizada
                            </span>
                            {lastSync && (
                                <span className="mt-1 text-[10px] text-emerald-400/40">
                                    Dados protegidos localmente
                                </span>
                            )}
                        </div>
                    )}
                    <button onClick={() => setModalOpen(true)} disabled={!reservationModuleEnabled || isOffline} className="btn-primary flex items-center gap-2 disabled:opacity-50">
                        <Plus className="h-4 w-4" />
                        Nova reserva
                    </button>
                </div>

            </div>

            {!reservationModuleEnabled && (
                <div className="card rounded-2xl border border-amber-500/30 bg-amber-500/10 p-5 text-sm text-amber-100">
                    O modulo de reservas foi desabilitado pela plataforma para este restaurante. Se o estabelecimento passar a atender em salao, reative o recurso no painel da plataforma.
                </div>
            )}

            {reservationModuleEnabled && <div className="card space-y-4 p-4">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                        <h2 className="text-lg font-bold text-white">Agenda visual do dia</h2>
                        <p className="mt-1 text-sm text-muted-foreground">
                            Veja por horario quais mesas estao ocupadas e onde ainda cabe nova reserva.
                        </p>
                    </div>
                    <div className="w-full max-w-xs">
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Data da agenda
                        </label>
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="input"
                        />
                    </div>
                </div>

                {!agendaConfig ? (
                    <div className="rounded-2xl border border-dashed border-[#2A2A2A] bg-[#121212] p-6 text-sm text-muted-foreground">
                        O restaurante esta fechado nesta data. Nao ha agenda de reservas para exibir.
                    </div>
                ) : !tableModuleEnabled ? (
                    <div className="rounded-2xl border border-dashed border-[#2A2A2A] bg-[#121212] p-6 text-sm text-muted-foreground">
                        O modulo de mesas foi desabilitado pela plataforma para este restaurante. A agenda visual por mesa fica indisponivel enquanto o estabelecimento operar sem salao proprio.
                    </div>
                ) : tables.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-[#2A2A2A] bg-[#121212] p-6 text-sm text-muted-foreground">
                        Nenhuma mesa cadastrada. Cadastre as mesas em Configuracoes para liberar a agenda automatica.
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="flex flex-wrap gap-3 text-sm">
                            <span className="badge badge-primary">Funcionamento {agendaConfig.open} - {agendaConfig.close}</span>
                            <span className="badge badge-muted">Slots de {slotMinutes} min</span>
                            <span className="badge badge-success">{tables.filter((table) => table.active).length} mesas ativas</span>
                        </div>
                        <div className="overflow-x-auto">
                            <div className="min-w-[760px]">
                                <div className="grid grid-cols-[120px,120px,120px,repeat(auto-fit,minmax(180px,1fr))] gap-3 border-b border-[#2A2A2A] pb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                    <div>Horario</div>
                                    <div>Reservas</div>
                                    <div>Pessoas</div>
                                    {tables.map((table) => (
                                        <div key={table.id}>{table.label}</div>
                                    ))}
                                </div>
                                <div className="mt-3 space-y-3">
                                    {agendaRows.map((row) => (
                                        <div key={row.slot} className="grid grid-cols-[120px,120px,120px,repeat(auto-fit,minmax(180px,1fr))] gap-3">
                                            <div className="rounded-xl border border-[#2A2A2A] bg-[#151515] p-3 font-semibold text-white">{row.slot}</div>
                                            <div className="rounded-xl border border-[#2A2A2A] bg-[#151515] p-3 text-white">{row.occupancy}</div>
                                            <div className="rounded-xl border border-[#2A2A2A] bg-[#151515] p-3 text-white">{row.totalGuests}</div>
                                            {row.tables.map((item) => (
                                                <div
                                                    key={`${row.slot}-${item.tableId}`}
                                                    className={`rounded-xl border p-3 text-sm ${
                                                        item.reservation
                                                            ? 'border-primary/30 bg-primary/10 text-white'
                                                            : 'border-[#2A2A2A] bg-[#111] text-muted-foreground'
                                                    }`}
                                                >
                                                    {item.reservation ? (
                                                        <div className="space-y-1">
                                                            <p className="font-semibold text-white">{item.reservation.customerName}</p>
                                                            <p>{item.reservation.partySize} pessoas</p>
                                                            <p className="text-xs">{statusMeta[item.reservation.status as keyof typeof statusMeta].label}</p>
                                                        </div>
                                                    ) : (
                                                        <span>Livre</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>}

            {reservationModuleEnabled && <div className="card flex flex-col gap-4 p-4 md:flex-row">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar por nome ou telefone..."
                        className="input pl-10"
                    />
                </div>
                <div className="flex gap-2 text-sm">
                    <span className="badge badge-warning">{reservations.filter((r) => r.status === 'pending').length} pendentes</span>
                    <span className="badge badge-success">{reservations.filter((r) => r.status === 'confirmed').length} confirmadas</span>
                    <span className="badge badge-muted">{reservations.filter((r) => r.status === 'completed').length} concluidas</span>
                </div>
            </div>}

            {reservationModuleEnabled && (loading ? (
                <div className="py-12 text-center text-muted-foreground">Carregando reservas...</div>
            ) : (
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    {filteredReservations.map((reservation) => (
                        <div key={reservation.id} className="card space-y-4 p-5">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <h2 className="text-lg font-bold text-white">{reservation.customerName}</h2>
                                    <p className="text-sm text-muted-foreground">{reservation.customerPhone}</p>
                                </div>
                                <span className={statusMeta[reservation.status as keyof typeof statusMeta].className}>
                                    {statusMeta[reservation.status as keyof typeof statusMeta].label}
                                </span>
                            </div>

                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div className="rounded-xl border border-[#2A2A2A] bg-[#1A1A1A] p-3">
                                    <p className="text-muted-foreground">Data</p>
                                    <p className="mt-1 font-semibold text-white">{formatDate(reservation.date)}</p>
                                </div>
                                <div className="rounded-xl border border-[#2A2A2A] bg-[#1A1A1A] p-3">
                                    <p className="text-muted-foreground">Horario</p>
                                    <p className="mt-1 font-semibold text-white">{reservation.time}</p>
                                </div>
                                <div className="rounded-xl border border-[#2A2A2A] bg-[#1A1A1A] p-3">
                                    <p className="text-muted-foreground">Pessoas</p>
                                    <p className="mt-1 font-semibold text-white">{reservation.partySize}</p>
                                </div>
                                <div className="rounded-xl border border-[#2A2A2A] bg-[#1A1A1A] p-3">
                                    <p className="text-muted-foreground">Observacoes</p>
                                    <p className="mt-1 truncate font-semibold text-white">{reservation.notes || 'Sem observacoes'}</p>
                                </div>
                                <div className="col-span-2 rounded-xl border border-[#2A2A2A] bg-[#1A1A1A] p-3">
                                    <p className="text-muted-foreground">Mesa atribuida</p>
                                    <p className="mt-1 font-semibold text-white">{reservation.table?.label || 'Nao atribuida'}</p>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                <button onClick={() => updateStatus(reservation.id, 'confirmed')} disabled={!!updatingReservationAction} className="btn-secondary flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-60">
                                    <CheckCircle2 className="h-4 w-4" />
                                    {updatingReservationAction === `${reservation.id}:confirmed` ? 'Salvando...' : 'Confirmar'}
                                </button>
                                <button onClick={() => updateStatus(reservation.id, 'completed')} disabled={!!updatingReservationAction} className="btn-secondary flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-60">
                                    <CheckCircle2 className="h-4 w-4" />
                                    {updatingReservationAction === `${reservation.id}:completed` ? 'Salvando...' : 'Concluir'}
                                </button>
                                <button onClick={() => updateStatus(reservation.id, 'pending')} disabled={!!updatingReservationAction} className="btn-secondary flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-60">
                                    <Clock3 className="h-4 w-4" />
                                    {updatingReservationAction === `${reservation.id}:pending` ? 'Salvando...' : 'Marcar pendente'}
                                </button>
                                <button onClick={() => updateStatus(reservation.id, 'cancelled')} disabled={!!updatingReservationAction} className="btn-secondary flex items-center gap-2 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-60">
                                    <XCircle className="h-4 w-4" />
                                    {updatingReservationAction === `${reservation.id}:cancelled` ? 'Salvando...' : 'Cancelar'}
                                </button>
                            </div>
                        </div>
                    ))}

                    {filteredReservations.length === 0 && (
                        <div className="card col-span-1 p-8 text-center text-muted-foreground xl:col-span-2">
                            <CalendarDays className="mx-auto mb-3 h-10 w-10 opacity-40" />
                            Nenhuma reserva encontrada.
                        </div>
                    )}
                </div>
            ))}

            <Modal
                isOpen={modalOpen}
                onClose={() => {
                    if (!reservationSaving) {
                        setModalOpen(false);
                    }
                }}
                title="Nova reserva"
                footer={(
                    <>
                        <button type="button" onClick={() => setModalOpen(false)} disabled={reservationSaving} className="btn-secondary disabled:cursor-not-allowed disabled:opacity-60">Cancelar</button>
                        <button type="submit" form="reservationForm" disabled={reservationSaving} className="btn-primary disabled:cursor-not-allowed disabled:opacity-60">{reservationSaving ? 'Salvando...' : 'Salvar reserva'}</button>
                    </>
                )}
            >
                <form id="reservationForm" onSubmit={createReservation} className="space-y-4">
                    <input name="customerName" required className="input" placeholder="Nome do cliente" />
                    <input name="customerPhone" required className="input" placeholder="Telefone" />
                    <div className="grid grid-cols-2 gap-4">
                        <input name="date" required type="date" className="input" defaultValue={selectedDate} />
                        <input name="time" required type="time" className="input" />
                    </div>
                    <input name="guests" required type="number" min="1" className="input" placeholder="Quantidade de pessoas" />
                    <textarea name="notes" className="input min-h-[100px] resize-none" placeholder="Observacoes" />
                </form>
            </Modal>
        </div>
    );
};

export default AdminReservations;
